import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  AttendanceEventSummary,
  CreateShiftChangesParams,
} from '@src/models/interfaces/attendanceEvent.interface';
import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type { TUserOrganization } from '@src/models/interfaces/user.interface';
import type {
  IWorkJourneyMatrixCell,
  IWorkJourneyMatrixUser,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { DateUtil } from '@src/packages/utils/dates';
import {
  isUsablePersonDisplayName,
  resolveUserDisplayName,
} from '@src/packages/utils/user';
import AttendanceEventService from '@src/services/AttendanceEventService';
import AttendanceRecordService from '@src/services/AttendanceRecordService';
import UserService from '@src/services/UserService';

import {
  applyShiftChangesToAttendanceRecord,
  applyShiftChangesToMatrixCell,
} from '../utils/applyOptimisticAttendanceEdit';
import {
  isRealAttendanceRecordId,
  resolveAttendanceAddress,
  type ResolvedAttendanceAddress,
} from '../utils/resolveAttendanceAddress';
import { selectMatrixAttendanceRecord } from '../utils/selectMatrixAttendanceRecord';

const attendanceService = new AttendanceRecordService();
const attendanceEventService = new AttendanceEventService();
const userService = new UserService();

export type MatrixAttendanceDrawerCellContext = Pick<
  IWorkJourneyMatrixCell,
  | 'kind'
  | 'code'
  | 'exceptionType'
  | 'leaveType'
  | 'observation'
  | 'attendanceCompleted'
  | 'description'
  | 'attendance'
>;

export type MatrixAttendanceDrawerTarget = {
  /** When missing or synthetic, the hook resolves the AR by employee + day. */
  attendanceRecordId?: string | null;
  userId: string;
  date: string;
  user: IWorkJourneyMatrixUser;
  cell?: MatrixAttendanceDrawerCellContext | null;
};

export type MatrixAttendanceDrawerData = {
  record: IAttendanceRecord;
  user: {
    id: string;
    displayName: string;
    photoURL?: string | null;
    phone?: string | null;
    email?: string | null;
  };
  address: ResolvedAttendanceAddress;
  /** Journey mark events for this record (may be empty). */
  events: AttendanceEventSummary[];
};

type UseWorkJourneyMatrixAttendanceDrawerParams = {
  organizationId?: string | null;
  workJourneyConfigId?: string | null;
};

/** Calendar-day bounds in the app default timezone (org / America/Santiago). */
const dayBoundsMs = (isoDate: string): { startMillis: number; endMillis: number } | null =>
  DateUtil.getCalendarDateBoundsMillis(isoDate);

const loadAttendanceRecord = async (
  organizationId: string,
  target: MatrixAttendanceDrawerTarget,
  workJourneyConfigId?: string | null,
): Promise<IAttendanceRecord | null> => {
  if (isRealAttendanceRecordId(target.attendanceRecordId)) {
    // The id comes from the matrix cell, so it is the record backing that cell
    // even when it belongs to another journey (substitute covering this one).
    const byId = (await attendanceService.getAttendanceRecord({
      organizationId,
      attendanceRecordId: target.attendanceRecordId as string,
    })) as IAttendanceRecord | null;
    if (byId) {
      return byId;
    }
  }

  const bounds = dayBoundsMs(target.date);
  if (!bounds) return null;

  const list = (await attendanceService.listByEmployeeInScheduledPeriod({
    organizationId,
    employeeId: target.userId,
    startMillis: bounds.startMillis,
    endMillis: bounds.endMillis,
  })) as IAttendanceRecord[];

  return selectMatrixAttendanceRecord(list, {
    workJourneyConfigId,
    cell: target.cell,
  });
};

export const useWorkJourneyMatrixAttendanceDrawer = ({
  organizationId,
  workJourneyConfigId,
}: UseWorkJourneyMatrixAttendanceDrawerParams) => {
  const [target, setTarget] = useState<MatrixAttendanceDrawerTarget | null>(null);
  const [data, setData] = useState<MatrixAttendanceDrawerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingChanges, setPendingChanges] = useState<CreateShiftChangesParams | null>(null);

  const open = useCallback((next: MatrixAttendanceDrawerTarget) => {
    setPendingChanges(null);
    setTarget(next);
  }, []);

  const close = useCallback(() => {
    setTarget(null);
    setData(null);
    setError(null);
    setLoading(false);
    setPendingChanges(null);
  }, []);

  /**
   * Shows a submitted edit while the Pub/Sub trigger applies it. Kept out of
   * `target` so it never retriggers the load effect with stale server data.
   */
  const applyPendingChanges = useCallback((changes: CreateShiftChangesParams) => {
    setPendingChanges(changes);
  }, []);

  useEffect(() => {
    if (!target || !organizationId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const [recordRaw, orgUser] = await Promise.all([
          loadAttendanceRecord(organizationId, target, workJourneyConfigId),
          userService.getUser(organizationId, target.userId) as Promise<TUserOrganization | null>,
        ]);

        if (cancelled) return;

        if (!recordRaw) {
          setError('not_found');
          return;
        }

        const recordId = typeof recordRaw.id === 'string' ? recordRaw.id : '';
        let events: AttendanceEventSummary[] = [];
        if (recordId) {
          try {
            events = await attendanceEventService.listByAttendanceRecord(
              organizationId,
              recordId,
            );
          } catch (eventsError) {
            // Map still shows the workplace; mark pins are best-effort.
            console.error('Error loading attendance events for matrix drawer:', eventsError);
            events = [];
          }
        }

        if (cancelled) return;

        const rawEmployeeFullname =
          typeof recordRaw.employeeFullname === 'string' ? recordRaw.employeeFullname : '';
        const employeeFullname = isUsablePersonDisplayName(rawEmployeeFullname)
          ? rawEmployeeFullname.trim()
          : '';
        const displayName =
          resolveUserDisplayName(orgUser) ||
          employeeFullname ||
          resolveUserDisplayName(target.user) ||
          target.userId;

        setData({
          record: recordRaw,
          user: {
            id: target.userId,
            displayName,
            photoURL: orgUser?.photoURL ?? target.user.photoURL ?? null,
            phone: orgUser?.phone ?? null,
            email: orgUser?.email ?? orgUser?.professionalEmail ?? null,
          },
          address: resolveAttendanceAddress(recordRaw.address),
          events,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId, target, workJourneyConfigId]);

  const visibleData = useMemo(() => {
    if (!data || !pendingChanges) return data;
    return { ...data, record: applyShiftChangesToAttendanceRecord(data.record, pendingChanges) };
  }, [data, pendingChanges]);

  const visibleTarget = useMemo(() => {
    if (!target || !pendingChanges || !target.cell) return target;
    return { ...target, cell: applyShiftChangesToMatrixCell(target.cell, pendingChanges) };
  }, [pendingChanges, target]);

  return {
    open,
    close,
    applyPendingChanges,
    target: visibleTarget,
    data: visibleData,
    loading,
    error,
    isOpen: Boolean(target),
  };
};
